import React, { useState, useEffect, useRef } from 'react';
import '../assets/styles/ChatOutputWithImage.css';
import FeedbackSuggestion from './FeedbackSuggestion';
import { FeedbackKeyType } from './ChatHistory';



interface FeedbackMessage {
    feedback: {
        [key in FeedbackKeyType]?: {
            score?: number;
            suggestion1?: string;
            suggestion2?: string;
        };
    };
}

interface ChatOutputWithImageProps {
    speakingStyle: string;
    showHalo: boolean;
    simType?: string; // Add simType prop
    feedbackMessages: { [key: number]: FeedbackMessage };
    showFeedback: boolean;
    imagePath: string | null;
    isLoadingImages: boolean;
    onToggleFullscreen?: () => void; // Add fullscreen toggle prop
}

const ChatOutputWithImage = ({ 
    speakingStyle, 
    showHalo, 
    simType = 'default',
    feedbackMessages,
    showFeedback,
    imagePath,
    isLoadingImages,
    onToggleFullscreen
}: ChatOutputWithImageProps) => {
    // Track number of rerenders (only in development)
    const renderCountRef = useRef(0);
    renderCountRef.current += 1;
    if (process.env.NODE_ENV === 'development' && renderCountRef.current % 10 === 0) {
      console.log(`[RENDER_COUNT] ChatOutputWithImage rendered ${renderCountRef.current} times`);
    }

    useEffect(() => {
     return () => {
       console.log('[EMOTION_DEBUG] 🗑️ ChatOutputWithImage unmounted');
     }
    }, []);





    // Get the most recent feedback message
    const getMostRecentFeedback = () => {
        if (!showFeedback || !feedbackMessages || Object.keys(feedbackMessages).length === 0) {
            return null;
        }
        
        // Find the most recent feedback message (highest key value with non-empty feedback)
        const keys = Object.keys(feedbackMessages).map(Number).sort((a, b) => b - a);
        for (const key of keys) {
            const feedbackMessage = feedbackMessages[key];
            if (feedbackMessage && feedbackMessage.feedback) {
                return feedbackMessage;
            }
        }
        return null;
    };

    const mostRecentFeedback = getMostRecentFeedback();

    // Show loading state while fetching images
    if (isLoadingImages) {
        return (
            <div className="chat-output-container">
                <div className="image-halo-container">
                    <div className="image-container">
                        <div className="loading-placeholder">Loading...</div>
                    </div>
                </div>
                <div className="speaking-style-label">
                    <span>{speakingStyle}</span>
                </div>
            </div>
        );
    }

    // Show placeholder if no images are available
    if (!imagePath) {
        return (
            <div className="chat-output-container">
                <div className="image-halo-container">
                    <div className="image-container">
                        <div className="no-image-placeholder">No Image</div>
                    </div>
                </div>
                <div className="speaking-style-label">
                    <span>{speakingStyle}</span>
                </div>
            </div>
        );
    }

    return (
        <div className="chat-output-container">
            <div >
                {/* Outer halo ring */}
                {/* <div className={` ${showHalo ? 'halo-outer' : ''}`}></div>

                <div className={` ${showHalo ? 'halo-middle' : ''}`}></div>

                <div className={` ${showHalo ? 'halo-inner' : ''}`}></div> 
                */}

                {/* Image container */}
                <div className="image-container">
                    <img
                        src={imagePath}
                        alt={`Speaking style: ${speakingStyle}`}
                        className="speaking-style-image"
                        onError={(e) => {
                            console.error(`Failed to load image: ${imagePath}`);
                            // Remove the fallback logic since we don't have default images
                            e.currentTarget.style.display = 'none';
                        }}
                    />
                    {/* Expand icon in bottom right corner */}
                    {onToggleFullscreen && (
                        <button
                            className="expand-icon"
                            onClick={onToggleFullscreen}
                            title="Enter Fullscreen (F)"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M7 14H5V19H10V17H7V14Z" fill="currentColor"/>
                                <path d="M5 10H7V7H10V5H5V10Z" fill="currentColor"/>
                                <path d="M17 14H19V19H14V17H17V14Z" fill="currentColor"/>
                                <path d="M14 5V7H17V10H19V5H14Z" fill="currentColor"/>
                            </svg>
                        </button>
                    )}
                </div>
            </div>

            {/* <div className="speaking-style-label">
               <span>{speakingStyle}</span> 
            </div> */}

            {/* Display most recent feedback */}
            {mostRecentFeedback && mostRecentFeedback.feedback &&
                Object.keys(mostRecentFeedback.feedback).map((key) => {
                    const feedback = mostRecentFeedback.feedback[key as FeedbackKeyType];
                    let showAlternateSuggestion = false;
                    if (feedback?.score && feedback.score >= 1) {
                        showAlternateSuggestion = true;
                    } else if (key === 'voice_qa' && feedback !== null) {
                        showAlternateSuggestion = true;
                    }
                    if (feedback?.suggestion1 === 'None') {
                        showAlternateSuggestion = false;
                    }
                    return showAlternateSuggestion && (
                        <FeedbackSuggestion
                            key={`feedback-${key}`}
                            feedbackKey={key}
                            feedback={{ score: feedback?.score, suggestion1: feedback?.suggestion1 }}
                        />
                    );
                })}
        </div>
    );
}

export default React.memo(ChatOutputWithImage);